import { Checkbox, Stack, Typography } from '@mui/material';
import {
  BackupStatus,
  NativeStandardConfigVaultProtectionModeEnum,
  ResourceType,
  TakeNativeSnapshotRequestVaultProtectionModeEnum,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { AllRegionsSelector } from '@/components/selectors/allRegionsSelector';
import { BackupVaultSelection } from '@/components/vaults/backupVaultSelection';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import type { BackupVault } from '@/data/vaults/backupVault';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import {
  areAllNativeBackupResources,
  isNativeCrossRegionUnsupported,
} from './inventoryResourceUtils';
import { getEligibleProviderVaults } from './providerVaultEligibility';

import { VaultProtectionOptions } from '../settings/backup-policies/AirGappedVaultOptions';
import { MIN_RETENTION_DAYS_FOR_AIR_GAPPED } from '../settings/backup-policies/BackupPolicySchedule';
import { RetentionSelector } from '../settings/controls/retentionSelector';

interface TakeSnapshotDialogProps {
  entities: InventoryResource[];
  onClose: () => void;
}

const INITIAL_RETENTION_DAYS = 30;

const vaultProtectionModeMap: Record<
  NativeStandardConfigVaultProtectionModeEnum,
  TakeNativeSnapshotRequestVaultProtectionModeEnum
> = {
  [NativeStandardConfigVaultProtectionModeEnum.None]:
    TakeNativeSnapshotRequestVaultProtectionModeEnum.None,
  [NativeStandardConfigVaultProtectionModeEnum.AirGapped]:
    TakeNativeSnapshotRequestVaultProtectionModeEnum.AirGapped,
  [NativeStandardConfigVaultProtectionModeEnum.VaultLockGovernance]:
    TakeNativeSnapshotRequestVaultProtectionModeEnum.VaultLockGovernance,
  [NativeStandardConfigVaultProtectionModeEnum.VaultLockCompliance]:
    TakeNativeSnapshotRequestVaultProtectionModeEnum.VaultLockCompliance,
};

function toSnapshotVaultProtectionMode(
  mode: NativeStandardConfigVaultProtectionModeEnum
): TakeNativeSnapshotRequestVaultProtectionModeEnum | undefined {
  const mapped = vaultProtectionModeMap[mode];
  return mapped === TakeNativeSnapshotRequestVaultProtectionModeEnum.None
    ? undefined
    : mapped;
}

export function TakeSnapshotDialog(props: TakeSnapshotDialogProps) {
  const { entities, onClose } = props;
  const {
    EnableAzureVaultNetworkIsolation,
    EnableCrossCloudBackup,
    EnableAirGappedVault,
  } = useFeatureFlags();
  const { snackbar } = useWorkspace();
  const [inProgress, setInProgress] = useState(false);
  const [pitrSnapshot, setPitrSnapshot] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [retentionInDays, setRetentionInDays] = useState(
    INITIAL_RETENTION_DAYS
  );
  const [selectedVault, setSelectedVault] = useState<BackupVault | undefined>();
  const [targetRegion, setTargetRegion] = useState(entities[0].region);
  const [vaultProtectionMode, setVaultProtectionMode] =
    useState<NativeStandardConfigVaultProtectionModeEnum>(
      NativeStandardConfigVaultProtectionModeEnum.None
    );
  const dal = useDAL();
  const { vaults, isLoading } = useBackupVaults();

  // Check if all resources support native backup (e.g., FSx/EFS use AWS Backup in customer account)
  const isNativeBackup = areAllNativeBackupResources(entities);

  const hasCrossRegionRestriction = entities.some(
    isNativeCrossRegionUnsupported
  );

  const providerVaults = useMemo(() => {
    // Native backups (FSx/EFS) don't use Eon vaults
    if (isNativeBackup) {
      return [];
    }

    const isDlsg = entities[0].backupStatus === BackupStatus.Dlsg;
    if (isDlsg) {
      return vaults.filter(
        (vault) =>
          vault.status === 'ACTIVE' &&
          vault.id === entities[0].dlsgPolicy?.policyPlan.backupSchedule.vaultId
      );
    }
    return getEligibleProviderVaults(vaults, entities, {
      EnableAzureVaultNetworkIsolation,
      EnableCrossCloudBackup,
    });
  }, [
    vaults,
    entities,
    EnableAzureVaultNetworkIsolation,
    EnableCrossCloudBackup,
    isNativeBackup,
  ]);

  useEffect(() => {
    // Auto-select first vault for non-native backups only
    if (!isNativeBackup && providerVaults?.length > 0) {
      setSelectedVault(providerVaults[0]);
    }
  }, [providerVaults, isNativeBackup]);

  useEffect(() => {
    if (
      vaultProtectionMode ===
        NativeStandardConfigVaultProtectionModeEnum.AirGapped &&
      retentionInDays < MIN_RETENTION_DAYS_FOR_AIR_GAPPED
    ) {
      setRetentionInDays(MIN_RETENTION_DAYS_FOR_AIR_GAPPED);
    }
  }, [vaultProtectionMode, retentionInDays]);

  const triggerTakeSnapshot = async () => {
    // For non-native backups, vault is required
    if (!isNativeBackup && !selectedVault) {
      return;
    }

    setInProgress(true);
    setError(undefined);

    try {
      await Promise.all(
        entities.map((resource) => {
          if (
            resource.backupStatus === BackupStatus.Terminated ||
            resource.backupStatus === BackupStatus.Disconnected ||
            resource.backupStatus === BackupStatus.Unsupported ||
            resource.backupStatus === BackupStatus.LimitExceeded
          ) {
            return Promise.resolve();
          }

          if (isNativeBackup) {
            const crossRegionTarget =
              targetRegion !== resource.region ? targetRegion : undefined;
            const vaultMode =
              toSnapshotVaultProtectionMode(vaultProtectionMode);
            return dal.jobs.backup.createNative(
              resource.id,
              retentionInDays,
              crossRegionTarget,
              vaultMode
            );
          }

          return dal.jobs.backup.create(
            resource.id,
            selectedVault!.id,
            retentionInDays,
            pitrSnapshot
          );
        })
      );
      snackbar.showMessage(
        `Backup job started for ${entities.map((x) => x.resourceName).join(', ')}`
      );
      onClose();
    } catch (error) {
      const err = error as Error;
      setError(err.message || 'Something went wrong...');
    } finally {
      setInProgress(false);
    }
  };

  const minRetentionDays =
    vaultProtectionMode ===
    NativeStandardConfigVaultProtectionModeEnum.AirGapped
      ? MIN_RETENTION_DAYS_FOR_AIR_GAPPED
      : 1;

  const isButtonDisabled = isNativeBackup
    ? isLoading || retentionInDays < minRetentionDays
    : isLoading || !selectedVault || retentionInDays < 1;

  // Dynamic subtitle based on resource type
  const dialogSubtitle = isNativeBackup
    ? hasCrossRegionRestriction
      ? 'Native backups are stored in your AWS Backup Vault. Cross-region backup is not supported for this resource type.'
      : 'Native backups are stored in your AWS Backup Vault. Select a different region for cross-region backup.'
    : 'Choose the retention period and vault for the snapshot.';

  const { isEmployee } = useUser();
  const { isDemo } = useEnvironment();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const devMode = isEmployee && !isDemo && prefResponse?.value?.isDevMode;

  return (
    <>
      <MainDialog
        isOpen={true}
        isLoading={isLoading}
        header={{
          title: 'Take on-demand snapshot',
          subtitle: dialogSubtitle,
          onClose,
        }}
        footer={{
          primaryButton: {
            'data-testid': 'take-snapshot-button',
            icon: 'material-symbols-backup-outline-rounded',
            text: 'Take Snapshot',
            disabled: isButtonDisabled,
            onClick: triggerTakeSnapshot,
          },
          secondaryButton: {
            'data-testid': 'cancel-take-snapshot-button',
            onClick: onClose,
            text: 'Cancel',
          },
          submitInProgress: inProgress,
          loadingText: 'Taking snapshot...',
          children: error ? (
            <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
              <i className='ri-error-warning-line mr-[8px] text-[18px]' />
              {error}
            </Stack>
          ) : null,
        }}
      >
        <Stack gap='24px'>
          <Stack gap='12px' paddingX='40px'>
            <Stack direction='row' gap='24px'>
              <Stack gap='12px' flex={1}>
                <Typography>Retention (calculated from now)</Typography>
                <RetentionSelector
                  onChange={setRetentionInDays}
                  retentionInDays={retentionInDays}
                  minimumRetentionDays={minRetentionDays}
                />
              </Stack>
              {isNativeBackup && (
                <Stack gap='12px' flex={1}>
                  <Typography>Region (in source account)</Typography>
                  <AllRegionsSelector
                    regionName={targetRegion}
                    setRegionName={setTargetRegion}
                    provider={entities[0].cloudProvider}
                    disabled={hasCrossRegionRestriction}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
          {isNativeBackup && EnableAirGappedVault && (
            <Stack paddingX='40px'>
              <VaultProtectionOptions
                vaultProtectionMode={vaultProtectionMode}
                onModeChange={setVaultProtectionMode}
                isLocked={false}
              />
            </Stack>
          )}
          {devMode &&
            entities.length === 1 &&
            (entities[0].resourceType === ResourceType.AzureSapHanaVm ||
              entities[0].resourceType === ResourceType.GcpSapHanaVm) && (
              <Stack gap='12px' paddingX='40px'>
                <Typography>Hana pitr snapshot</Typography>
                <Checkbox
                  checked={pitrSnapshot}
                  onChange={(e) => setPitrSnapshot(e.target.checked)}
                />
              </Stack>
            )}
          {!isNativeBackup && (
            <Stack gap='12px'>
              <Typography paddingLeft='40px'>Choose a vault:</Typography>
              <Stack
                width='100%'
                overflow='auto'
                padding='4px 24px 4px 40px'
                marginY='12px'
                direction='row'
                gap='24px'
              >
                <BackupVaultSelection
                  onSelect={setSelectedVault}
                  selectedVaults={selectedVault ? [selectedVault.id] : []}
                  loading={isLoading}
                  vaults={providerVaults}
                  shouldWrap={false}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </MainDialog>
    </>
  );
}
